import { computed, toRef } from "vue";
import { useOrganizationPositionsViewState } from "./useViewState";
import { useDropdownItems } from "@/composables/dropdown/useDropdownItems";

// Store callbacks for events
let eventCallbacks = {
  onCloseClick: null,
  onPreparingCloseClick: null,
  onPreparingAvatarsClick: null,
  onPreparingEffectiveDateClick: null,
  onRowChatterClick: null,
  onMenuClick: null,
  onPositionClick: null,
};

export function setEventCallbacks(callbacks) {
  eventCallbacks = { ...eventCallbacks, ...callbacks };
}

export function useTableConfig(viewStateParam = null) {
  // Use provided viewState or create new one (for backward compatibility)
  const viewState = viewStateParam || useOrganizationPositionsViewState();
  const dropdownItems = useDropdownItems();

  const initializeDropdownFlags = async () => {
    // Only initialize flags if the method exists (account version has it, storybook doesn't)
    if (dropdownItems.initializeFlags && typeof dropdownItems.initializeFlags === 'function') {
      await dropdownItems.initializeFlags();
    }
  };

  const isNotEmpty = (val) => {
    if (Array.isArray(val)) {
      return val.length > 0;
    }
    if (val && typeof val === "object") {
      return Object.keys(val).length > 0;
    }
    return false;
  };

  function getEmployeeInfo(employee) {
    if (!employee || typeof employee !== "object") {
      return { photoUrl: "", firstName: "", lastName: "" };
    }

    const nameParts = (
      employee.firstName && employee.lastName
        ? `${employee.firstName} ${employee.lastName}`
        : employee.employeeName || employee.assigned_employee_name || ""
    ).split(" ");

    return {
      photoUrl:
        employee.photoUrl ||
        employee.employeePicture ||
        employee.assigned_employee_avatar ||
        "",
      firstName: nameParts[0] || "",
      lastName: nameParts[1] || "",
    };
  }

  // Helper to extract position code from code_str (e.g., "#44 - Customer Service Representative" -> "44")
  function getPositionCode(item) {
    // Check if already mapped format
    if (item.position?.position_code) {
      return item.position.position_code.toString().replace(/^#/, "");
    }
    // Handle raw API response format
    const codeMatch = item.code_str?.match(/#(\d+)/);
    return codeMatch ? codeMatch[1] : item.code?.toString() || "";
  }

  // Helper to get position name from either format
  function getPositionName(item) {
    return item.position?.position_name || item.name || "";
  }

  // Helper to get roles count
  function getRolesCount(item) {
    return (
      item.occupiedBy?.rolesCount ||
      item.position?.roles_count ||
      item.roles_count ||
      0
    );
  }

  // Helper to get responsibilities count
  function getResponsibilitiesCount(item) {
    return (
      item.occupiedBy?.responsibilityCount ||
      item.position?.responsibilities_count ||
      item.responsibilities_count ||
      0
    );
  }

  // Helper to build occupiedBy data from either format
  function getOccupiedByData(item) {
    // If already in mapped format
    if (item.occupiedBy) {
      return item.occupiedBy;
    }
    // Map from raw API response
    const hasOccupied =
      item.assigned_primary !== null && item.assigned_primary !== undefined;
    return {
      employeeName: item.assigned_primary?.assigned_employee_name || "",
      employeePicture: item.assigned_primary?.assigned_employee_avatar || "",
      hasPrimaryOccupation: hasOccupied,
      hasVacant: item.vacancy > 0 && !hasOccupied,
      fteText: item.capacity || 1,
      effectiveDate: item.primary_position_effective_on || "",
      rolesCount: item.roles_count || 0,
      responsibilityCount: item.responsibilities_count || 0,
      employeeId: item.assigned_primary?.assigned_employee_id || "",
    };
  }

  // Helper to build preparingFor data from either format
  function getPreparingForData(item) {
    // If already in mapped format
    if (item.preparingFor) {
      return item.preparingFor;
    }
    // Map from raw API response
    const preparingEmployees = (item.assigned_preparing_for || []).map(
      (emp) => ({
        employeeName: emp.assigned_employee_name || "",
        employeePicture: emp.assigned_employee_avatar || "",
        firstName: emp.assigned_employee_name?.split(" ")[0] || "",
        lastName: emp.assigned_employee_name?.split(" ")[1] || "",
        photoUrl: emp.assigned_employee_avatar || "",
        effectiveDate: item.preparing_as_position_effective_on || "",
      })
    );

    return {
      preparingEmployees,
      roles: item.roles_count || 0,
      responsibilities: item.responsibilities_count || 0,
      effectiveDate: item.preparing_as_position_effective_on || "",
      employeeId: preparingEmployees[0]?.assigned_employee_id || "",
      type: preparingEmployees.length > 0 ? "preparing" : "vacant",
    };
  }

  // Helper to get status
  function getStatus(item) {
    return item.status || "draft";
  }

  // Helper to get archived
  function getArchived(item) {
    return item.archived || false;
  }

  // Helper function to convert filter values to display strings for chips
  // Returns both display values and a mapping to original values for removal
  const getFilterDisplayValues = (filterValues) => {
    if (!Array.isArray(filterValues))
      return { displayValues: [], valueMap: [] };

    const displayValues = [];
    const valueMap = []; // Maps display value back to original filter value

    filterValues.forEach((val) => {
      // If it's a string (status filter), return as is
      if (typeof val === "string") {
        displayValues.push(val);
        valueMap.push(val); // Map display value to itself
      } else if (val && typeof val === "object") {
        // If it's an object (radio button filter), return the value/label
        // Skip "No Filter" values as they shouldn't be displayed as chips
        const displayValue = val.value || val.label || "";
        // Don't show "No Filter" as a chip
        if (displayValue !== "No Filter") {
          displayValues.push(displayValue);
          valueMap.push(val); // Map display value back to original object
        }
      }
    });

    return { displayValues, valueMap };
  };

  const tableParams = computed(() => {
    // IMPORTANT: Access reactive properties directly inside computed to track properly
    const currentFilterValues = [...(viewState.state.filters.values || [])];
    const currentSearchText = viewState.state.filters.searchText || "";
    // Convert filter values to display strings for chips
    const { displayValues: filterDisplayValues, valueMap } =
      getFilterDisplayValues(currentFilterValues);

    // Store the value map on the viewState so removeFilters can access it
    viewState.state.filters._displayValueMap = valueMap;

    return {
      headers: [
        {
          text: "Position",
          sortable: true,
          value: "name",
        },
        {
          text: "Occupied by",
          sortable: false,
          value: "occupiedBy",
          multiple: true,
          data: [
            {
              text: "Occupied by ",
              img: "employee-organization/primary-assignment-grey.svg",
            },
          ],
        },
        {
          text: "Preparing",
          sortable: false,
          value: "preparingFor",
          img: "icons/preparing.svg",
        },
        {
          text: "Status",
          sortable: false,
          value: "status",
        },
        {
          text: "",
          sortable: false,
          value: "chatter",
        },
        {
          text: "",
          sortable: false,
          value: "actions",
        },
      ],
      columns: [
        {
          field: "",
          component: "NewPositionCard",
          props: (item) => {
            // Format position code using helper
            const code = getPositionCode(item);

            // Get position capacity object
            const positionCapacity = {
              validRolesCount: item.valid_roles_count || 0,
              invalidRolesCount: item.invalid_roles_count || 0,
              validResponsibilitiesCount:
                item.active_responsibilities_count || 0,
              capacity: item.capacity,
              allocation: item.vacancy,
            };

            // Get created_on date
            const createdOn =
              item.position?.created_on || item.created_on
                ? new Date(item.position?.created_on || item.created_on)
                : new Date();

            return {
              class: "my-4 datatable-text-content",
              "data-position-id": item?.id,
              position_capacity: positionCapacity,
              position_name: getPositionName(item),
              position_code: code,
              created_on: createdOn,
              hovered: true,
              active: item.status === 'active',
            };
          },
          events: (item) => ({
            clickValidityBadge: () => {
              eventCallbacks.onPositionClick(item);
            },
            positionClick: () => {
              if (eventCallbacks.onPositionClick) {
                eventCallbacks.onPositionClick(item);
              }
            },
          }),
        },
        {
          field: "",
          component: "PrimaryPositionOccupationTag",
          props: (item) => {
            const occupiedBy = getOccupiedByData(item);
            return {
              class: "occupied_content my-4",
              employee1name: occupiedBy.employeeName || "",
              employee1picture: occupiedBy.employeePicture || "",
              hasPrimaryOccupation: occupiedBy.hasPrimaryOccupation || false,
              hasVacant: occupiedBy.hasVacant || false,
              positionCode: getPositionCode(item),
              positionName: getPositionName(item),
              fteText: occupiedBy.fteText || 1,
              effective_date: occupiedBy.effectiveDate || "",
              rolesCount: occupiedBy.rolesCount || 0,
              responsibilityCount: occupiedBy.responsibilityCount || 0,
              employeeId: occupiedBy.employeeId || "",
              addHoverEffect: true,
            };
          },
          events: (item) => ({
            onClose: () => {
              viewState.onRelievePrimaryAssignment(item);
              if (eventCallbacks.onCloseClick) {
                eventCallbacks.onCloseClick(item);
              }
            },
          }),
        },
        {
          field: "",
          component: "PreparePositionOccupdationTag",
          props: (item) => {
            const preparingFor = getPreparingForData(item);
            const preparingEmployees = preparingFor.preparingEmployees || [];
            const employeeInfo =
              preparingEmployees.length > 0
                ? getEmployeeInfo(preparingEmployees[0])
                : {};

            return {
              class: "occupied_content my-4",
              positionName: getPositionName(item),
              positionCode: getPositionCode(item),
              roles: preparingFor.roles || 0,
              responsibilities: preparingFor.responsibilities || 0,
              nameUrl: preparingFor.nameUrl || "",
              occupiedList: preparingFor.occupiedList || [],
              occupiedByList: preparingFor.occupiedByList || [],
              preparingEmployees: preparingEmployees.map((emp) => ({
                ...getEmployeeInfo(emp),
                effective_date: emp.effectiveDate || "",
              })),
              effectiveDate: preparingFor.effectiveDate || "",
              addHoverEffect: true,
              employeeId: preparingFor.employeeId || "",
              type: preparingFor.type || "vacant",
            };
          },
          events: (item) => ({
            onAvatarClick: () => {
              viewState.onPreparingAvatarsClick(item);
              if (eventCallbacks.onPreparingAvatarsClick) {
                eventCallbacks.onPreparingAvatarsClick(item);
              }
            },
            onClose: () => {
              viewState.onRelievePrepareAssignment(item);
              if (eventCallbacks.onPreparingCloseClick) {
                eventCallbacks.onPreparingCloseClick(item);
              }
            },
            onHandleApplyDate: (event) => {
              viewState.onPreparingEffectiveDateClick(event, item);
              if (eventCallbacks.onPreparingEffectiveDateClick) {
                eventCallbacks.onPreparingEffectiveDateClick(event, item);
              }
            },
          }),
        },
        {
          field: "",
          component: "Badge",
          props: (item) => ({
            status: getStatus(item),
            archived: getArchived(item),
            access: "has-access",
            showTooltip: false,
          }),
        },
        {
          field: "",
          component: "ChatterButton",
          props: () => ({
            class: "my-4",
          }),
          events: (item) => ({
            onClick: () => {
              viewState.openPositionChatterModal(item);
            },
          }),
        },
        {
          field: "",
          component: "Dropdown",
          props: (item) => ({
            "data-testid": "position-operations-menu",
            translateXOffset: 12,
            translateYOffset: 30,
            isOpen: false,
            dropdownItems: dropdownItems.getOrgObjectDropdownItems(item || {}),
          }),
          events: (item) => ({
            onClick: async (operation) => {
              await viewState.handlePositionDropdownOperation(operation, item);
              if (eventCallbacks.onMenuClick) {
                eventCallbacks.onMenuClick({ item, operation });
              }
            },
          }),
        },
      ],
      filters: {
        values: filterDisplayValues, // Use display values for chips
        searchText: currentSearchText,
        rawValues: currentFilterValues, // Keep original structure for filtering logic
      },
      hideTopSection: viewState.config.hideTopSection || false,
      hideActiveNo: viewState.config.hideActiveNo || false,
      activeRecords: {
        field: "status",
        value: "active",
      },
      defaultOrderingField: "name",
      itemsPerPage: 10,
      activeRecordsCount: viewState.config.activeRecordsCount,
      actionLabel: viewState.config.actionLabel || "Add Position",
      hideGridView: true,
      title: viewState.config.title || "Positions Organization",
      orderingStorageKey: "organizationPositions.ordering",
      showPositionsViewSelector: true,
      positionsViewSelection:  viewState.config.positionsViewSelection,
      show_vacancies: false,
      getRowStyle: (item) => {
        if (item?.status === "active") {
          return { backgroundColor: "#FFFFFF" }; //#ECFDF3
        } else if (item?.status === "retired") {
          return { backgroundColor: "#FFFFFF" }; //#e0e0e0
        }
        return { backgroundColor: "#FFFFFF" };
      },
    };
  });

  return {
    tableParams,
    initializeDropdownFlags,
  };
}
